import React, { useEffect } from 'react';

const RateLimiting = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Rate Limiting Algorithms - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master rate limiting with Token Bucket, Leaky Bucket, and Fixed Window algorithms. Learn API throttling and traffic control strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master rate limiting with Token Bucket, Leaky Bucket, and Fixed Window algorithms. Learn API throttling and traffic control strategies.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Rate Limiting Algorithms</h1>
        <p style={styles.subtitle}>
          Control system load with Token Bucket, Leaky Bucket, and advanced throttling strategies
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Rate Limiting?</h2>
          <p style={styles.paragraph}>
            Rate limiting is a technique used to control the rate at which requests are processed by a system. 
            It prevents abuse, ensures fair resource usage, and protects systems from being overwhelmed by too 
            many requests in a short period. Rate limiting is essential for API management, DDoS protection, 
            and maintaining system stability.
          </p>
          <p style={styles.paragraph}>
            Effective rate limiting helps maintain quality of service for legitimate users while preventing 
            malicious actors from consuming excessive resources. It's a critical component of system reliability 
            and security architecture.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Token Bucket Algorithm</h2>
          
          <h3 style={styles.heading}>How Token Bucket Works</h3>
          <p style={styles.paragraph}>
            The Token Bucket algorithm maintains a bucket that holds tokens. Tokens are added to the bucket at a 
            fixed rate, and each request consumes one or more tokens. If tokens are available, the request is 
            processed; otherwise, it's rejected or queued.
          </p>
          <pre style={styles.codeBlock}>
{`Token Bucket Algorithm:

Concept:
- Bucket holds tokens (capacity limit)
- Tokens added at fixed rate (refill rate)
- Each request consumes tokens
- Allow bursts up to bucket capacity
- Smooth out traffic over time

Visual Representation:
    Tokens Added
         ↓
    ┌─────────────┐
    │   🪙🪙🪙🪙   │ ← Bucket (capacity: 10)
    │   🪙🪙🪙🪙   │
    │   🪙🪙      │
    └─────────────┘
         ↓
    Request consumes tokens

Parameters:
- Bucket Capacity: Maximum tokens that can be stored
- Refill Rate: Tokens added per time unit
- Token Cost: Tokens consumed per request

Advantages:
✓ Allows traffic bursts
✓ Smooth long-term rate
✓ Simple to implement
✓ Memory efficient

Disadvantages:
✗ Can allow large bursts
✗ Complex burst behavior
✗ Requires token management`}
          </pre>

          <h3 style={styles.heading}>Token Bucket Implementation</h3>
          <p style={styles.paragraph}>
            Here's a comprehensive implementation of the Token Bucket algorithm:
          </p>
          <pre style={styles.codeBlock}>
{`Token Bucket Implementation:

import time
import threading
from typing import Optional

class TokenBucket:
    def __init__(self, capacity: int, refill_rate: float, refill_period: float = 1.0):
        """
        Initialize Token Bucket
        
        Args:
            capacity: Maximum number of tokens in bucket
            refill_rate: Tokens added per refill_period
            refill_period: Time period for refill (seconds)
        """
        self.capacity = capacity
        self.tokens = capacity  # Start with full bucket
        self.refill_rate = refill_rate
        self.refill_period = refill_period
        self.last_refill = time.time()
        self.lock = threading.Lock()
    
    def _refill(self):
        """Refill tokens based on elapsed time"""
        now = time.time()
        elapsed = now - self.last_refill
        
        if elapsed >= self.refill_period:
            # Calculate tokens to add
            periods_elapsed = elapsed / self.refill_period
            tokens_to_add = periods_elapsed * self.refill_rate
            
            # Add tokens (don't exceed capacity)
            self.tokens = min(self.capacity, self.tokens + tokens_to_add)
            self.last_refill = now
    
    def consume(self, tokens: int = 1) -> bool:
        """
        Try to consume tokens from bucket
        
        Args:
            tokens: Number of tokens to consume
            
        Returns:
            True if tokens were consumed, False otherwise
        """
        with self.lock:
            self._refill()
            
            if self.tokens >= tokens:
                self.tokens -= tokens
                return True
            return False
    
    def peek(self) -> float:
        """Get current token count without consuming"""
        with self.lock:
            self._refill()
            return self.tokens
    
    def wait_for_tokens(self, tokens: int = 1, timeout: Optional[float] = None) -> bool:
        """
        Wait until tokens are available
        
        Args:
            tokens: Number of tokens needed
            timeout: Maximum time to wait (None = wait forever)
            
        Returns:
            True if tokens were acquired, False if timeout
        """
        start_time = time.time()
        
        while True:
            if self.consume(tokens):
                return True
            
            if timeout and (time.time() - start_time) >= timeout:
                return False
            
            # Calculate wait time until next refill
            with self.lock:
                self._refill()
                if self.tokens >= tokens:
                    continue
                
                # Wait for next refill period
                time_to_wait = self.refill_period - (time.time() - self.last_refill)
                if time_to_wait > 0:
                    time.sleep(min(time_to_wait, 0.1))

# Usage Examples
# API Rate Limiter
class APIRateLimiter:
    def __init__(self):
        # 100 requests per minute, burst up to 20
        self.bucket = TokenBucket(capacity=20, refill_rate=100/60)  # ~1.67 tokens/sec
        self.user_buckets = {}
    
    def is_allowed(self, user_id: str = None) -> bool:
        """Check if request is allowed"""
        if user_id:
            # Per-user rate limiting
            if user_id not in self.user_buckets:
                self.user_buckets[user_id] = TokenBucket(
                    capacity=10, 
                    refill_rate=60/60  # 60 requests per minute
                )
            return self.user_buckets[user_id].consume()
        else:
            # Global rate limiting
            return self.bucket.consume()
    
    def get_remaining_tokens(self, user_id: str = None) -> float:
        """Get remaining tokens for debugging"""
        if user_id and user_id in self.user_buckets:
            return self.user_buckets[user_id].peek()
        return self.bucket.peek()

# Flask Integration Example
from flask import Flask, request, jsonify

app = Flask(__name__)
rate_limiter = APIRateLimiter()

@app.before_request
def check_rate_limit():
    user_id = request.headers.get('X-User-ID')
    
    if not rate_limiter.is_allowed(user_id):
        return jsonify({
            'error': 'Rate limit exceeded',
            'retry_after': 60
        }), 429

@app.route('/api/data')
def get_data():
    return jsonify({'data': 'Your API response'})

# Advanced Token Bucket with Different Token Costs
class WeightedTokenBucket(TokenBucket):
    def __init__(self, capacity: int, refill_rate: float):
        super().__init__(capacity, refill_rate)
        self.operation_costs = {
            'read': 1,
            'write': 3,
            'delete': 5,
            'admin': 10
        }
    
    def is_operation_allowed(self, operation: str) -> bool:
        """Check if operation is allowed based on its cost"""
        cost = self.operation_costs.get(operation, 1)
        return self.consume(cost)

# Usage
weighted_limiter = WeightedTokenBucket(capacity=50, refill_rate=10)

# Different operations consume different amounts of tokens
if weighted_limiter.is_operation_allowed('read'):
    # Process read operation (costs 1 token)
    pass

if weighted_limiter.is_operation_allowed('admin'):
    # Process admin operation (costs 10 tokens)
    pass`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Leaky Bucket Algorithm</h2>
          
          <h3 style={styles.heading}>How Leaky Bucket Works</h3>
          <p style={styles.paragraph}>
            The Leaky Bucket algorithm maintains a queue (bucket) that processes requests at a fixed rate. 
            Requests are added to the bucket, and if the bucket overflows, excess requests are dropped or rejected.
          </p>
          <pre style={styles.codeBlock}>
{`Leaky Bucket Algorithm:

Concept:
- Bucket holds requests (queue)
- Requests processed at fixed rate
- Overflow requests are dropped
- Smooths irregular traffic
- Provides consistent output rate

Visual Representation:
    Incoming Requests
         ↓
    ┌─────────────┐
    │ Req Req Req │ ← Bucket (queue)
    │ Req Req     │
    │             │
    └─────────────┘
         ↓
    Fixed Rate Processing

Parameters:
- Bucket Size: Maximum queue capacity
- Leak Rate: Requests processed per time unit
- Queue Policy: FIFO, Priority, etc.

Advantages:
✓ Consistent output rate
✓ Smooths traffic spikes
✓ Predictable behavior
✓ Simple concept

Disadvantages:
✗ No burst allowance
✗ May drop requests
✗ Higher memory usage
✗ Processing delay`}
          </pre>

          <h3 style={styles.heading}>Leaky Bucket Implementation</h3>
          <p style={styles.paragraph}>
            Here's an implementation of the Leaky Bucket algorithm with queue management:
          </p>
          <pre style={styles.codeBlock}>
{`Leaky Bucket Implementation:

import time
import threading
from queue import Queue, Full, Empty
from typing import Callable, Any, Optional

class LeakyBucket:
    def __init__(self, capacity: int, leak_rate: float, processor: Callable = None):
        """
        Initialize Leaky Bucket
        
        Args:
            capacity: Maximum queue size
            leak_rate: Items processed per second
            processor: Function to process leaked items
        """
        self.capacity = capacity
        self.leak_rate = leak_rate
        self.processor = processor or self._default_processor
        
        self.queue = Queue(maxsize=capacity)
        self.last_leak = time.time()
        self.lock = threading.Lock()
        self.running = True
        
        # Start background leak thread
        self.leak_thread = threading.Thread(target=self._leak_worker, daemon=True)
        self.leak_thread.start()
    
    def _default_processor(self, item):
        """Default processor - just print the item"""
        print(f"Processing: {item}")
    
    def add_request(self, item: Any) -> bool:
        """
        Add request to bucket
        
        Args:
            item: Request to add
            
        Returns:
            True if added successfully, False if bucket is full
        """
        try:
            self.queue.put_nowait(item)
            return True
        except Full:
            return False
    
    def _leak_worker(self):
        """Background worker that processes items at leak rate"""
        while self.running:
            try:
                # Calculate time since last leak
                now = time.time()
                time_since_leak = now - self.last_leak
                
                # Calculate how many items to leak
                items_to_leak = int(time_since_leak * self.leak_rate)
                
                if items_to_leak > 0:
                    for _ in range(items_to_leak):
                        try:
                            item = self.queue.get_nowait()
                            self.processor(item)
                            self.queue.task_done()
                        except Empty:
                            break
                    
                    self.last_leak = now
                
                # Sleep to maintain leak rate
                sleep_time = 1.0 / self.leak_rate if self.leak_rate > 0 else 1.0
                time.sleep(min(sleep_time, 0.1))
                
            except Exception as e:
                print(f"Error in leak worker: {e}")
    
    def stop(self):
        """Stop the leaky bucket"""
        self.running = False
        self.leak_thread.join()
    
    def get_queue_size(self) -> int:
        """Get current queue size"""
        return self.queue.qsize()
    
    def is_full(self) -> bool:
        """Check if bucket is full"""
        return self.queue.full()

# API Request Handler with Leaky Bucket
class LeakyBucketRateLimiter:
    def __init__(self):
        self.buckets = {}
        self.default_capacity = 10
        self.default_leak_rate = 2.0  # 2 requests per second
    
    def get_bucket(self, user_id: str) -> LeakyBucket:
        """Get or create bucket for user"""
        if user_id not in self.buckets:
            self.buckets[user_id] = LeakyBucket(
                capacity=self.default_capacity,
                leak_rate=self.default_leak_rate,
                processor=self._process_request
            )
        return self.buckets[user_id]
    
    def _process_request(self, request_data):
        """Process the actual request"""
        # This would contain your actual request processing logic
        print(f"Processing request: {request_data}")
    
    def handle_request(self, user_id: str, request_data: Any) -> tuple[bool, str]:
        """
        Handle incoming request
        
        Returns:
            (success, message) tuple
        """
        bucket = self.get_bucket(user_id)
        
        if bucket.add_request(request_data):
            return True, "Request queued for processing"
        else:
            return False, "Rate limit exceeded - bucket full"
    
    def get_queue_status(self, user_id: str) -> dict:
        """Get queue status for user"""
        if user_id in self.buckets:
            bucket = self.buckets[user_id]
            return {
                'queue_size': bucket.get_queue_size(),
                'is_full': bucket.is_full(),
                'capacity': bucket.capacity
            }
        return {'queue_size': 0, 'is_full': False, 'capacity': self.default_capacity}

# Usage Example
rate_limiter = LeakyBucketRateLimiter()

# Simulate incoming requests
def simulate_requests():
    user_id = "user123"
    
    for i in range(15):  # Send 15 requests quickly
        success, message = rate_limiter.handle_request(
            user_id, 
            f"Request {i+1}"
        )
        
        status = rate_limiter.get_queue_status(user_id)
        print(f"Request {i+1}: {message} (Queue: {status['queue_size']}/{status['capacity']})")
        
        time.sleep(0.1)  # Small delay between requests

# Priority Leaky Bucket
class PriorityLeakyBucket:
    def __init__(self, capacity: int, leak_rate: float):
        self.capacity = capacity
        self.leak_rate = leak_rate
        self.high_priority_queue = Queue(maxsize=capacity//2)
        self.normal_priority_queue = Queue(maxsize=capacity//2)
        self.last_leak = time.time()
        self.running = True
        
        self.leak_thread = threading.Thread(target=self._priority_leak_worker, daemon=True)
        self.leak_thread.start()
    
    def add_request(self, item: Any, priority: str = 'normal') -> bool:
        """Add request with priority"""
        queue = self.high_priority_queue if priority == 'high' else self.normal_priority_queue
        
        try:
            queue.put_nowait(item)
            return True
        except Full:
            return False
    
    def _priority_leak_worker(self):
        """Process high priority requests first"""
        while self.running:
            try:
                now = time.time()
                time_since_leak = now - self.last_leak
                items_to_leak = int(time_since_leak * self.leak_rate)
                
                if items_to_leak > 0:
                    for _ in range(items_to_leak):
                        # Try high priority first
                        try:
                            item = self.high_priority_queue.get_nowait()
                            self._process_item(item, 'HIGH')
                        except Empty:
                            # Then normal priority
                            try:
                                item = self.normal_priority_queue.get_nowait()
                                self._process_item(item, 'NORMAL')
                            except Empty:
                                break
                    
                    self.last_leak = now
                
                time.sleep(0.1)
                
            except Exception as e:
                print(f"Error in priority leak worker: {e}")
    
    def _process_item(self, item, priority):
        print(f"[{priority}] Processing: {item}")

# Usage
priority_bucket = PriorityLeakyBucket(capacity=20, leak_rate=3.0)

# Add different priority requests
priority_bucket.add_request("Normal request 1", 'normal')
priority_bucket.add_request("Critical request 1", 'high')
priority_bucket.add_request("Normal request 2", 'normal')
priority_bucket.add_request("Critical request 2", 'high')`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Fixed Window Algorithm</h2>
          
          <h3 style={styles.heading}>How Fixed Window Works</h3>
          <p style={styles.paragraph}>
            The Fixed Window algorithm divides time into fixed windows and allows a specific number of requests 
            per window. It's simple to implement but can allow traffic spikes at window boundaries.
          </p>
          <pre style={styles.codeBlock}>
{`Fixed Window Algorithm:

Concept:
- Time divided into fixed windows
- Counter tracks requests per window
- Reset counter at window boundary
- Simple and memory efficient

Visual Representation:
Window 1    Window 2    Window 3
|-------|  |-------|  |-------|
0 1 2 3 4  0 1 2     0 1 2 3
^ Requests  ^ Reset   ^ Requests

Time: 0s----10s----20s----30s
Limit: 5 requests per 10-second window

Advantages:
✓ Simple implementation
✓ Memory efficient
✓ Predictable behavior
✓ Easy to understand

Disadvantages:
✗ Traffic spikes at boundaries
✗ Uneven request distribution
✗ "Thundering herd" problem
✗ Less smooth than other algorithms`}
          </pre>

          <h3 style={styles.heading}>Fixed Window Implementation</h3>
          <p style={styles.paragraph}>
            Here's an implementation of the Fixed Window algorithm with Redis backend:
          </p>
          <pre style={styles.codeBlock}>
{`Fixed Window Implementation:

import time
import threading
from collections import defaultdict
from typing import Dict, Optional

class FixedWindowRateLimiter:
    def __init__(self, window_size: int, max_requests: int):
        """
        Initialize Fixed Window Rate Limiter
        
        Args:
            window_size: Window duration in seconds
            max_requests: Maximum requests per window
        """
        self.window_size = window_size
        self.max_requests = max_requests
        self.windows: Dict[str, Dict[int, int]] = defaultdict(dict)
        self.lock = threading.Lock()
    
    def _get_window_key(self, timestamp: float) -> int:
        """Get window key for given timestamp"""
        return int(timestamp // self.window_size)
    
    def is_allowed(self, identifier: str, timestamp: Optional[float] = None) -> tuple[bool, dict]:
        """
        Check if request is allowed
        
        Args:
            identifier: Unique identifier (user_id, IP, etc.)
            timestamp: Request timestamp (defaults to current time)
            
        Returns:
            (allowed, info) tuple
        """
        if timestamp is None:
            timestamp = time.time()
        
        window_key = self._get_window_key(timestamp)
        
        with self.lock:
            # Clean old windows (optional optimization)
            self._cleanup_old_windows(identifier, window_key)
            
            # Get current window count
            current_count = self.windows[identifier].get(window_key, 0)
            
            if current_count < self.max_requests:
                # Allow request
                self.windows[identifier][window_key] = current_count + 1
                
                return True, {
                    'allowed': True,
                    'current_count': current_count + 1,
                    'max_requests': self.max_requests,
                    'window_start': window_key * self.window_size,
                    'window_end': (window_key + 1) * self.window_size,
                    'reset_time': (window_key + 1) * self.window_size
                }
            else:
                # Reject request
                return False, {
                    'allowed': False,
                    'current_count': current_count,
                    'max_requests': self.max_requests,
                    'window_start': window_key * self.window_size,
                    'window_end': (window_key + 1) * self.window_size,
                    'reset_time': (window_key + 1) * self.window_size,
                    'retry_after': (window_key + 1) * self.window_size - timestamp
                }
    
    def _cleanup_old_windows(self, identifier: str, current_window: int):
        """Remove old window data to save memory"""
        if identifier in self.windows:
            # Keep only current and previous window
            windows_to_keep = {current_window - 1, current_window}
            self.windows[identifier] = {
                k: v for k, v in self.windows[identifier].items()
                if k in windows_to_keep
            }
    
    def get_remaining_requests(self, identifier: str, timestamp: Optional[float] = None) -> int:
        """Get remaining requests in current window"""
        if timestamp is None:
            timestamp = time.time()
        
        window_key = self._get_window_key(timestamp)
        current_count = self.windows[identifier].get(window_key, 0)
        return max(0, self.max_requests - current_count)

# Redis-backed Fixed Window (Production Ready)
import redis

class RedisFixedWindowRateLimiter:
    def __init__(self, redis_client: redis.Redis, window_size: int, max_requests: int):
        self.redis = redis_client
        self.window_size = window_size
        self.max_requests = max_requests
    
    def is_allowed(self, identifier: str, timestamp: Optional[float] = None) -> tuple[bool, dict]:
        """Redis-backed rate limiting"""
        if timestamp is None:
            timestamp = time.time()
        
        window_key = int(timestamp // self.window_size)
        redis_key = f"rate_limit:{identifier}:{window_key}"
        
        # Use Redis pipeline for atomic operations
        pipe = self.redis.pipeline()
        pipe.incr(redis_key)
        pipe.expire(redis_key, self.window_size + 1)  # +1 for safety
        results = pipe.execute()
        
        current_count = results[0]
        
        if current_count <= self.max_requests:
            return True, {
                'allowed': True,
                'current_count': current_count,
                'max_requests': self.max_requests,
                'reset_time': (window_key + 1) * self.window_size
            }
        else:
            return False, {
                'allowed': False,
                'current_count': current_count,
                'max_requests': self.max_requests,
                'reset_time': (window_key + 1) * self.window_size,
                'retry_after': (window_key + 1) * self.window_size - timestamp
            }

# Flask Integration
from flask import Flask, request, jsonify

app = Flask(__name__)
rate_limiter = FixedWindowRateLimiter(window_size=60, max_requests=100)  # 100 req/minute

@app.before_request
def check_rate_limit():
    # Use IP address as identifier
    client_ip = request.remote_addr
    allowed, info = rate_limiter.is_allowed(client_ip)
    
    if not allowed:
        response = jsonify({
            'error': 'Rate limit exceeded',
            'current_count': info['current_count'],
            'max_requests': info['max_requests'],
            'retry_after': int(info['retry_after'])
        })
        response.status_code = 429
        response.headers['X-RateLimit-Limit'] = str(info['max_requests'])
        response.headers['X-RateLimit-Remaining'] = '0'
        response.headers['X-RateLimit-Reset'] = str(int(info['reset_time']))
        response.headers['Retry-After'] = str(int(info['retry_after']))
        return response
    
    # Add rate limit headers to successful responses
    response = request.environ.get('werkzeug.response')
    if response:
        remaining = rate_limiter.get_remaining_requests(client_ip)
        response.headers['X-RateLimit-Limit'] = str(info['max_requests'])
        response.headers['X-RateLimit-Remaining'] = str(remaining)
        response.headers['X-RateLimit-Reset'] = str(int(info['reset_time']))

# Multi-tier Rate Limiting
class MultiTierFixedWindow:
    def __init__(self):
        self.limiters = {
            'per_second': FixedWindowRateLimiter(1, 10),      # 10/second
            'per_minute': FixedWindowRateLimiter(60, 300),    # 300/minute  
            'per_hour': FixedWindowRateLimiter(3600, 5000),   # 5000/hour
            'per_day': FixedWindowRateLimiter(86400, 50000)   # 50000/day
        }
    
    def is_allowed(self, identifier: str) -> tuple[bool, dict]:
        """Check against all rate limits"""
        results = {}
        
        for tier, limiter in self.limiters.items():
            allowed, info = limiter.is_allowed(identifier)
            results[tier] = info
            
            if not allowed:
                return False, {
                    'allowed': False,
                    'violated_tier': tier,
                    'details': results
                }
        
        return True, {
            'allowed': True,
            'details': results
        }

# Usage
multi_limiter = MultiTierFixedWindow()
allowed, info = multi_limiter.is_allowed('user123')

if not allowed:
    print(f"Rate limit exceeded at {info['violated_tier']} level")
else:
    print("Request allowed")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Sliding Window Algorithm</h2>
          
          <h3 style={styles.heading}>Sliding Window Log</h3>
          <p style={styles.paragraph}>
            The Sliding Window Log algorithm maintains a log of request timestamps and allows requests based on 
            the count within a sliding time window. It provides accurate rate limiting but requires more memory.
          </p>
          <pre style={styles.codeBlock}>
{`Sliding Window Log Implementation:

import time
import bisect
import threading
from collections import defaultdict, deque
from typing import List, Optional

class SlidingWindowLog:
    def __init__(self, window_size: int, max_requests: int):
        """
        Initialize Sliding Window Log Rate Limiter
        
        Args:
            window_size: Window duration in seconds
            max_requests: Maximum requests per window
        """
        self.window_size = window_size
        self.max_requests = max_requests
        self.request_logs: defaultdict[str, deque] = defaultdict(deque)
        self.lock = threading.Lock()
    
    def is_allowed(self, identifier: str, timestamp: Optional[float] = None) -> tuple[bool, dict]:
        """Check if request is allowed"""
        if timestamp is None:
            timestamp = time.time()
        
        with self.lock:
            # Clean old requests
            self._cleanup_old_requests(identifier, timestamp)
            
            # Check current count
            current_count = len(self.request_logs[identifier])
            
            if current_count < self.max_requests:
                # Allow request and log it
                self.request_logs[identifier].append(timestamp)
                
                return True, {
                    'allowed': True,
                    'current_count': current_count + 1,
                    'max_requests': self.max_requests,
                    'window_start': timestamp - self.window_size,
                    'window_end': timestamp
                }
            else:
                # Calculate when next request will be allowed
                oldest_request = self.request_logs[identifier][0]
                retry_after = oldest_request + self.window_size - timestamp
                
                return False, {
                    'allowed': False,
                    'current_count': current_count,
                    'max_requests': self.max_requests,
                    'retry_after': max(0, retry_after)
                }
    
    def _cleanup_old_requests(self, identifier: str, current_time: float):
        """Remove requests outside the sliding window"""
        window_start = current_time - self.window_size
        log = self.request_logs[identifier]
        
        # Remove old requests from the left
        while log and log[0] <= window_start:
            log.popleft()
    
    def get_request_rate(self, identifier: str, timestamp: Optional[float] = None) -> float:
        """Get current request rate (requests per second)"""
        if timestamp is None:
            timestamp = time.time()
        
        with self.lock:
            self._cleanup_old_requests(identifier, timestamp)
            current_count = len(self.request_logs[identifier])
            return current_count / self.window_size

# Sliding Window Counter (Memory Optimized)
class SlidingWindowCounter:
    def __init__(self, window_size: int, max_requests: int, num_buckets: int = 10):
        """
        Memory-optimized sliding window using sub-windows
        
        Args:
            window_size: Total window duration
            max_requests: Max requests per window
            num_buckets: Number of sub-windows for approximation
        """
        self.window_size = window_size
        self.max_requests = max_requests
        self.num_buckets = num_buckets
        self.bucket_size = window_size / num_buckets
        
        self.counters: defaultdict[str, List[int]] = defaultdict(
            lambda: [0] * num_buckets
        )
        self.last_update: defaultdict[str, float] = defaultdict(float)
        self.lock = threading.Lock()
    
    def _get_bucket_index(self, timestamp: float) -> int:
        """Get bucket index for timestamp"""
        return int((timestamp % self.window_size) / self.bucket_size)
    
    def _update_buckets(self, identifier: str, timestamp: float):
        """Update bucket counters, clearing old buckets"""
        last_update = self.last_update[identifier]
        
        if timestamp - last_update >= self.bucket_size:
            # Calculate how many buckets to clear
            buckets_to_clear = min(
                self.num_buckets,
                int((timestamp - last_update) / self.bucket_size)
            )
            
            current_bucket = self._get_bucket_index(timestamp)
            
            # Clear old buckets
            for i in range(buckets_to_clear):
                bucket_to_clear = (current_bucket - i) % self.num_buckets
                self.counters[identifier][bucket_to_clear] = 0
            
            self.last_update[identifier] = timestamp
    
    def is_allowed(self, identifier: str, timestamp: Optional[float] = None) -> tuple[bool, dict]:
        """Check if request is allowed using sliding window approximation"""
        if timestamp is None:
            timestamp = time.time()
        
        with self.lock:
            self._update_buckets(identifier, timestamp)
            
            # Calculate current count across all buckets
            current_count = sum(self.counters[identifier])
            
            if current_count < self.max_requests:
                # Increment current bucket
                bucket_index = self._get_bucket_index(timestamp)
                self.counters[identifier][bucket_index] += 1
                
                return True, {
                    'allowed': True,
                    'current_count': current_count + 1,
                    'max_requests': self.max_requests
                }
            else:
                return False, {
                    'allowed': False,
                    'current_count': current_count,
                    'max_requests': self.max_requests
                }

# Distributed Sliding Window with Redis
class RedisSlidingWindow:
    def __init__(self, redis_client: redis.Redis, window_size: int, max_requests: int):
        self.redis = redis_client
        self.window_size = window_size
        self.max_requests = max_requests
    
    def is_allowed(self, identifier: str, timestamp: Optional[float] = None) -> tuple[bool, dict]:
        """Redis-backed sliding window using sorted sets"""
        if timestamp is None:
            timestamp = time.time()
        
        key = f"sliding_window:{identifier}"
        window_start = timestamp - self.window_size
        
        # Lua script for atomic sliding window check
        lua_script = """
        local key = KEYS[1]
        local window_start = tonumber(ARGV[1])
        local timestamp = tonumber(ARGV[2])
        local max_requests = tonumber(ARGV[3])
        local window_size = tonumber(ARGV[4])
        
        -- Remove old entries
        redis.call('ZREMRANGEBYSCORE', key, '-inf', window_start)
        
        -- Count current entries
        local current_count = redis.call('ZCARD', key)
        
        if current_count < max_requests then
            -- Add new entry
            redis.call('ZADD', key, timestamp, timestamp)
            redis.call('EXPIRE', key, window_size + 1)
            return {1, current_count + 1}
        else
            return {0, current_count}
        end
        """
        
        result = self.redis.eval(
            lua_script,
            1,
            key,
            window_start,
            timestamp,
            self.max_requests,
            self.window_size
        )
        
        allowed = bool(result[0])
        current_count = result[1]
        
        return allowed, {
            'allowed': allowed,
            'current_count': current_count,
            'max_requests': self.max_requests
        }

# Usage Examples
# Accurate sliding window
log_limiter = SlidingWindowLog(window_size=60, max_requests=100)

# Memory-optimized sliding window
counter_limiter = SlidingWindowCounter(
    window_size=60, 
    max_requests=100, 
    num_buckets=12  # 5-second sub-windows
)

# Test the limiters
for i in range(105):  # Send 105 requests
    allowed, info = log_limiter.is_allowed(f"user123")
    if not allowed:
        print(f"Request {i+1} blocked. Retry after: {info['retry_after']:.2f}s")
        break
    else:
        print(f"Request {i+1} allowed. Count: {info['current_count']}")
    
    time.sleep(0.1)  # Small delay between requests`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Twitter API Rate Limiting</h3>
          <p style={styles.paragraph}>
            Twitter uses a combination of rate limiting algorithms with different limits for different endpoints. 
            They provide rate limit headers and implement both user-based and application-based limits with 
            15-minute windows for most endpoints.
          </p>

          <h3 style={styles.heading}>GitHub API Rate Limiting</h3>
          <p style={styles.paragraph}>
            GitHub implements a token bucket algorithm with 5000 requests per hour for authenticated users. 
            They provide detailed rate limit information in response headers and implement secondary rate limits 
            for resource-intensive operations.
          </p>

          <h3 style={styles.heading}>Stripe API Rate Limiting</h3>
          <p style={styles.paragraph}>
            Stripe uses sophisticated rate limiting with different limits for different types of operations. 
            They implement both request-based and resource-based rate limiting, with special handling for 
            payment processing to ensure reliability.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right algorithm:</strong> Token bucket for burst tolerance, leaky bucket for 
              smooth output, fixed window for simplicity, sliding window for accuracy.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper error responses:</strong> Return meaningful error messages with retry 
              information and appropriate HTTP status codes (429).
            </li>
            <li style={styles.listItem}>
              <strong>Use distributed rate limiting:</strong> For multi-server deployments, use Redis or 
              other shared storage for consistent rate limiting across instances.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor and alert:</strong> Track rate limiting metrics, blocked requests, and false 
              positives to tune your limits appropriately.
            </li>
            <li style={styles.listItem}>
              <strong>Implement graceful degradation:</strong> Consider queuing or throttling instead of 
              outright rejection for better user experience.
            </li>
            <li style={styles.listItem}>
              <strong>Provide rate limit headers:</strong> Include X-RateLimit-* headers to help clients 
              understand and adapt to rate limits.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Rate limiting is essential for protecting systems from abuse and ensuring fair resource usage. 
            Choose the appropriate algorithm based on your specific requirements for burst handling, memory 
            usage, and accuracy.
          </p>
          <p style={styles.paragraph}>
            Implement rate limiting early in your system design, monitor its effectiveness, and be prepared to 
            adjust limits based on actual usage patterns. Remember that rate limiting is just one part of a 
            comprehensive system protection strategy.
          </p>
        </section>
      </main>
    </article>
  );
};

export default RateLimiting;
